
import React from 'react';
import type { DayOfWeek, Language } from '../types';
import { DAYS_OF_WEEK } from '../types';
import { getTranslation, isRTL } from '../utils/translations';
import RotateCcwIcon from './icons/RotateCcwIcon';
import CalendarCheckIcon from './icons/CalendarCheckIcon';
import FastForwardIcon from './icons/FastForwardIcon';

interface StaticWeekBarProps {
    selectedDays: Set<DayOfWeek>;
    onToggleDay: (day: DayOfWeek) => void;
    onSetDays: (days: DayOfWeek[]) => void;
    language?: Language;
}

const ABBREVIATIONS_HE: Record<string, string> = {
    'Sunday': 'א', 'Monday': 'ב', 'Tuesday': 'ג', 'Wednesday': 'ד', 'Thursday': 'ה', 'Friday': 'ו', 'Saturday': 'ש'
};

const ABBREVIATIONS_EN: Record<string, string> = {
    'Sunday': 'S', 'Monday': 'M', 'Tuesday': 'T', 'Wednesday': 'W', 'Thursday': 'T', 'Friday': 'F', 'Saturday': 'S'
};

const StaticWeekBar: React.FC<StaticWeekBarProps> = ({ selectedDays, onToggleDay, onSetDays, language }) => {
    const t = (key: any) => getTranslation(language, key);
    const isHe = language === 'he';
    const rtl = isRTL(language);

    const handleNext3Days = () => {
        const today = new Date().getDay(); // 0-6 (Sun-Sat)
        const days: DayOfWeek[] = [];
        for (let i = 0; i < 3; i++) {
            days.push(DAYS_OF_WEEK[(today + i) % 7]);
        }
        onSetDays(days);
    };

    return (
        <div className="flex flex-col gap-4 p-4 bg-white border-b border-slate-100">
            {/* Quick Actions Row */}
            <div className={`flex flex-row justify-center gap-3 mb-1 ${rtl ? 'flex-row-reverse' : ''}`}>
                <button 
                    onClick={() => onSetDays([])}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-all active:scale-95"
                >
                    <RotateCcwIcon className="w-3.5 h-3.5" />
                    <span>{t('reset')}</span>
                </button>

                <button 
                    onClick={() => onSetDays([...DAYS_OF_WEEK])}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-brand-sage text-brand-sage bg-brand-sage/10 text-xs font-bold transition-all active:scale-95 shadow-sm"
                >
                    <CalendarCheckIcon className="w-3.5 h-3.5" />
                    <span>{t('allWeek')}</span>
                </button>

                <button 
                    onClick={handleNext3Days}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-sky-600 text-sky-600 bg-white hover:bg-sky-50 text-xs font-bold transition-all active:scale-95"
                >
                    <FastForwardIcon className="w-3.5 h-3.5" />
                    <span>{t('next3Days')}</span>
                </button>
            </div>

            {/* Static 7-Day Bar */}
            <div className="flex justify-between items-center w-full px-1">
                {DAYS_OF_WEEK.map((day) => {
                    const isActive = selectedDays.has(day);
                    const label = isHe ? ABBREVIATIONS_HE[day] : ABBREVIATIONS_EN[day];
                    
                    return (
                        <button
                            key={day}
                            onClick={() => onToggleDay(day)}
                            className={`
                                w-10 h-10 rounded-full flex items-center justify-center text-sm transition-all duration-200
                                ${isActive 
                                    ? 'bg-slate-900 text-white font-black shadow-lg scale-110' 
                                    : 'bg-slate-100 text-slate-400 font-bold hover:bg-slate-200'}
                            `}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default StaticWeekBar;
